import { FC, useMemo, useState } from 'react';
import { SearchFilter } from '@components/Filters/SearchFilter';
import {
  Stack,
  Table,
  TableColumn,
  TableNoRecords,
} from '@servicetitan/design-system';
import { Tooltip } from '@servicetitan/anvil2';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import ErrorComponent from '@components/ErrorComponent';
import { OutboundCallDetails } from '../../types';
import { OutboundCallEntity } from '@lib/types/common-call-types';
import RadioCell from './RadioCell';
import EstimatedWaitCell from './EstimatedWaitCell';
import QueueNameCell from './QueueNameCell';
import { QueueRow } from './types';
import styles from './QueuesTable.module.css';
import strings from './strings';

interface OnlineEmployeesHeaderCellProps {
  onClick?: React.MouseEventHandler<HTMLElement>;
  children?: React.ReactNode;
}

/**
 * Module-level component so it has a stable identity across re-renders of
 * `QueuesTable`. The parent re-renders on every RTCS update; with an inline
 * function Kendo would treat each render as a new component type and remount
 * the Tooltip, dropping its open state and producing a flash on hover.
 *
 * Rendered as a span with the Kendo `k-link` class so the click target fills
 * the entire header cell — same affordance as the default sortable headers.
 * Keeping it non-anchor, non-button is intentional (SonarQube S6848 flagged
 * the previous `<a>`); `role='button'`, `tabIndex={0}` and a keyboard handler
 * keep it accessible.
 */
const OnlineEmployeesHeaderCell: FC<OnlineEmployeesHeaderCellProps> = ({
  onClick,
  children,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(e as unknown as React.MouseEvent<HTMLSpanElement>);
    }
  };

  return (
    <span
      className='k-link'
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <Tooltip placement='top'>
        <Tooltip.Trigger>
          <span className={styles.onlineEmployeesLabel}>
            {strings.header.availableEmployees}
          </span>
        </Tooltip.Trigger>
        <Tooltip.Content>
          {strings.header.availableEmployeesTooltip}
        </Tooltip.Content>
      </Tooltip>
      {children}
    </span>
  );
};

const PAGE_SIZE = 4;

/**
 * Shared queue table used in both the outbound call modal and transfer modal.
 * Owns all UI state: search, sort, pagination, and row selection.
 *
 * `initialSort` controls the default sort order, which differs per context
 * (e.g. `estimatedWait asc` for outbound, `name asc` for transfer).
 *
 * The Queue (name) cell renders an inline warning icon when the row's
 * `hasFallbackWarning` flag is set. That flag is only populated by
 * the transfer-flow hook when the feature flag is enabled, so no additional
 * gating is needed at this layer.
 *
 * `useStrictAvailability` renames the employees column to "Online Employees"
 * and adds a header tooltip describing the stricter Available count (matching
 * the call center monitoring Capacity widget). The actual count is computed
 * upstream by the queue-state hooks based on the same flag.
 */
interface QueuesTableProps {
  queues: QueueRow[];
  loading: boolean;
  setCallDetails: (details: OutboundCallDetails) => void;
  initialSort?: SortDescriptor[];
  useStrictAvailability?: boolean;
}

const QueuesTable: FC<QueuesTableProps> = ({
  queues,
  loading,
  setCallDetails,
  initialSort = [],
  useStrictAvailability = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQueue, setSelectedQueue] = useState('');
  const [sort, setSort] = useState<SortDescriptor[]>(initialSort);
  const [page, setPage] = useState({ skip: 0, take: PAGE_SIZE });

  const filteredQueues = useMemo(() => {
    const filtered = !searchTerm
      ? queues
      : queues.filter((q) =>
          q.name.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    return orderBy(filtered, sort);
  }, [searchTerm, queues, sort]);

  const rows = useMemo(
    () => filteredQueues.slice(page.skip, page.skip + page.take),
    [filteredQueues, page],
  );

  const handlePageChange = (e: { page: { take: number; skip: number } }) => {
    if (page.take !== e.page.take) {
      setPage({ skip: 0, take: e.page.take });
    } else {
      setPage({ skip: e.page.skip, take: e.page.take });
    }
  };

  return (
    <Stack direction='column' spacing={2}>
      <Stack.Item>
        <SearchFilter
          placeholder='Search for a queue'
          className='w-100'
          value={searchTerm}
          onChange={setSearchTerm}
        />
      </Stack.Item>
      <Stack.Item>
        <Table
          borders={{ vertical: false, horizontal: true }}
          loading={loading}
          data={rows}
          sortable={true}
          sort={sort}
          onSortChange={(e) => setSort(e.sort)}
          pageable={{ buttonCount: 3 }}
          total={filteredQueues.length}
          skip={page.skip}
          pageSize={page.take}
          striped={false}
          onPageChange={handlePageChange}
        >
          <TableNoRecords>
            <div className='ta-center m-x-auto p-y-6' style={{ width: 420 }}>
              <ErrorComponent
                image='block'
                title='No results found'
                body='Try another search.'
              />
            </div>
          </TableNoRecords>
          <TableColumn
            width={40}
            cell={(props) =>
              RadioCell({
                dataItem: props.dataItem,
                selectedQueue: selectedQueue,
                onSelect: (queue) => {
                  setSelectedQueue(queue.id);
                  setCallDetails({
                    entity: OutboundCallEntity.Queue,
                    number: queue.number,
                    callDisplayName: queue.name,
                    queueId: queue.id,
                  });
                },
              })
            }
          />
          <TableColumn field='name' title='Queue' cell={QueueNameCell} />
          <TableColumn
            field='availableUsers'
            title={
              useStrictAvailability
                ? strings.header.availableEmployees
                : strings.header.employees
            }
            headerCell={
              useStrictAvailability ? OnlineEmployeesHeaderCell : undefined
            }
          />
          <TableColumn
            field='estimatedWait'
            title='Est. Wait Time'
            cell={(props) =>
              EstimatedWaitCell({
                dataItem: props.dataItem,
              })
            }
          />
        </Table>
      </Stack.Item>
    </Stack>
  );
};

export default QueuesTable;
